import {
  Alert,
  AlertIcon,
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Embed, models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import React, { useEffect, useMemo, useState } from "react";
import { Tenant } from "../../Models/Tenant";

type ReportType = "MAIN" | "USAGE";

type PowerBiEmbeddableConfig = {
  embedUrl?: string;
  accessToken?: string;
};

interface IPowerBiReportTabsProps {
  tenant?: Tenant | null;
  typeOfReport?: ReportType;
}

const getWorkspaceId = (typeOfReport: ReportType) => {
  const envName = process.env.NODE_ENV || "development";

  if (typeOfReport === "MAIN") {
    if (envName === "production") {
      return process.env.REACT_APP_POWERBI_MAIN_WORKSPACE_ID;
    }
    return process.env.REACT_APP_POWERBI_MAIN_WORKSPACE_ID || "";
  }

  if (envName === "production") {
    return process.env.REACT_APP_POWERBI_USAGE_WORKSPACE_ID;
  }

  return process.env.REACT_APP_POWERBI_USAGE_WORKSPACE_ID || "";
};

const getBaseUrl = () => {
  return (
    process.env.REACT_APP_POWERBI_FUNCTION_BASEURL ||
    "https://func-commandcenter-teamsintegration-dev.azurewebsites.net/api/"
  );
};

const getFunctionKey = () => {
  return process.env.REACT_APP_POWERBI_FUNCTION_KEY || "";
};

const getReportId = async (tenant: Tenant, typeOfReport: ReportType) => {
  const reportUrl =
    typeOfReport === "MAIN"
      ? `${getBaseUrl()}TenantReportId?code=${getFunctionKey()}&tid=${tenant.azureTenantId}`
      : `${getBaseUrl()}TenantUsageReportId?code=${getFunctionKey()}&tid=${tenant.azureTenantId}`;

  const resp = await fetch(reportUrl);
  const text = await resp.text();

  if (!text || text === "null" || text === "undefined") {
    return tenant[typeOfReport === "MAIN" ? "powerBIReportId" : "powerBIUsageReportId"] || "";
  }

  return text.trim();
};

const getEmbedConfig = async (tenant: Tenant, typeOfReport: ReportType) => {
  const resolvedReportId =
    tenant[typeOfReport === "MAIN" ? "powerBIReportId" : "powerBIUsageReportId"] ||
    (await getReportId(tenant, typeOfReport));

  if (!resolvedReportId || !tenant.azureTenantId) {
    return undefined;
  }

  const embedUrl = `${getBaseUrl()}PowerBIEmbeddedToken?code=${getFunctionKey()}&reportId=${resolvedReportId}&workspaceId=${getWorkspaceId(typeOfReport)}`;
  const response = await fetch(embedUrl);
  const data = await response.json();

  if (!data || !data.EmbedUrl || !data.EmbedToken) {
    return undefined;
  }

  return {
    embedUrl: data.EmbedUrl,
    accessToken: data.EmbedToken,
  } as PowerBiEmbeddableConfig;
};

export const PowerBiReportTabs: React.FC<IPowerBiReportTabsProps> = ({
  tenant,
  typeOfReport = "MAIN",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportConfig, setReportConfig] = useState<models.IReportEmbedConfiguration>({
    type: "report",
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    settings: { navContentPaneEnabled: false },
  });

  const reportTitle = useMemo(
    () => (typeOfReport === "MAIN" ? "Report" : "Usage Report"),
    [typeOfReport]
  );

  useEffect(() => {
    const loadReport = async () => {
      if (!tenant?.azureTenantId) {
        setReportConfig({
          ...reportConfig,
          embedUrl: undefined,
          accessToken: undefined,
        });
        return;
      }

      setIsLoading(true);
      try {
        const config = await getEmbedConfig(tenant, typeOfReport);
        setReportConfig((current) => ({
          ...current,
          embedUrl: config?.embedUrl,
          accessToken: config?.accessToken,
        }));
      } finally {
        setIsLoading(false);
      }
    };

    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.azureTenantId, typeOfReport]);

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={3} minH="320px">
        <Spinner size="md" />
        <Text>Loading {reportTitle.toLowerCase()}...</Text>
      </Box>
    );
  }

  if (!reportConfig.embedUrl || !reportConfig.accessToken) {
    return (
      <Alert status="info" fontSize="sm">
        <AlertIcon />
        No {reportTitle.toLowerCase()} data available for this tenant.
      </Alert>
    );
  }

  return (
    <Box height="72vh" minH="420px">
      <PowerBIEmbed
        embedConfig={reportConfig}
        cssClassName="report-style-class"
        getEmbeddedComponent={(embedObject: Embed) => {
          console.log(
            `Embedded object of type "${embedObject.embedtype}" received`
          );
        }}
      />
    </Box>
  );
};
